import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, Tag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FilterChip } from '@/components/ui/Chip';
import { Input } from '@/components/ui/Input';
import { MenuPopover } from '@/components/ui/Popover';
import { collectQuestionnaireTagsByUsage } from '@/lib/questionnaireTags';
import { cn } from '@/utils/cn';

export const TAB_TAG_CHIP_MAX = 6;
export const ROW_TAG_CHIP_MAX = 2;

export function QuestionnaireTagChips({
  tags,
  max = ROW_TAG_CHIP_MAX,
  emptyLabel,
}: {
  tags: readonly string[];
  max?: number;
  emptyLabel?: string;
}) {
  if (tags.length === 0) {
    // Same `.tg` row as the chips. A bare inline label sits beside the name.
    return emptyLabel ? (
      <span className="tg">
        <span className="none">{emptyLabel}</span>
      </span>
    ) : null;
  }
  const visible = tags.slice(0, max);
  const hidden = tags.slice(max);
  return (
    <span className="tg">
      {visible.map((tag) => (
        <span key={tag} className="tagc">
          {tag}
        </span>
      ))}
      {hidden.length > 0 ? (
        <span className="tagc more" title={tags.join(', ')}>
          +{hidden.length}
        </span>
      ) : null}
    </span>
  );
}

function toggleTag(selected: readonly string[], tag: string): string[] {
  return selected.includes(tag) ? selected.filter((item) => item !== tag) : [...selected, tag];
}

function TagChecklist({
  tags,
  selected,
  onToggle,
}: {
  tags: readonly string[];
  selected: readonly string[];
  onToggle: (tag: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const filtered = tags.filter((tag) => tag.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="tag-pop" onClick={(event) => event.stopPropagation()}>
      <Input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('forms.tags_search', 'Søg i tags')}
        aria-label={t('forms.tags_search', 'Søg i tags')}
        leadingIcon={<Search className="i sm" aria-hidden />}
      />
      <ul className="tag-pop-l" role="listbox" aria-multiselectable="true">
        {filtered.map((tag) => {
          const on = selected.includes(tag);
          return (
            <li key={tag}>
              <button
                type="button"
                role="option"
                aria-selected={on}
                className={cn('tag-pop-opt', on && 'on')}
                onClick={() => onToggle(tag)}
              >
                <span className={cn('cb', on && 'on')} aria-hidden>
                  {on ? <Check /> : null}
                </span>
                {tag}
              </button>
            </li>
          );
        })}
      </ul>
      {filtered.length === 0 ? (
        <p className="pk-hint">{t('forms.tags_none_match', 'Ingen tags matcher.')}</p>
      ) : null}
    </div>
  );
}

export function QuestionnaireTagFilter({
  tags,
  selected,
  onChange,
  labelled = true,
  variant = 'chips',
  usageSource,
}: {
  tags: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  labelled?: boolean;
  variant?: 'chips' | 'dropdown';
  usageSource?: ReadonlyArray<{ tags?: readonly string[] | null }>;
}) {
  const { t } = useTranslation();
  const ordered = useMemo(
    () => (usageSource ? collectQuestionnaireTagsByUsage(usageSource) : [...tags]),
    [tags, usageSource],
  );
  const available = ordered.filter((tag) => tags.includes(tag));
  if (available.length === 0 && selected.length === 0) return null;

  function toggle(tag: string) {
    onChange(toggleTag(selected, tag));
  }

  if (variant === 'dropdown') {
    return (
      <div className="pk-tags sk-tags tag-dd" role="group" aria-label={t('forms.tags_label', 'Tags')}>
        <MenuPopover
          align="start"
          title={t('forms.tags_label', 'Tags')}
          className="tag-pop-wrap"
          trigger={
            <button type="button" className="tag-dd-btn">
              <Tag className="i sm" aria-hidden />
              {t('forms.tags_label', 'Tags')}
              {selected.length > 0 ? <span className="n num">{selected.length}</span> : null}
              <ChevronDown className="i sm" aria-hidden />
            </button>
          }
        >
          <TagChecklist tags={available} selected={selected} onToggle={toggle} />
        </MenuPopover>
      </div>
    );
  }

  const face = available.slice(0, TAB_TAG_CHIP_MAX);
  const overflow = available.slice(TAB_TAG_CHIP_MAX);
  const extraSelected = selected.filter((tag) => !face.includes(tag));

  return (
    <div className="sk-tags" role="group" aria-label={t('forms.tags_label', 'Tags')}>
      {labelled ? (
        <span className="lb">
          <Tag className="i sm" aria-hidden />
          {t('forms.tags_label', 'Tags')}
        </span>
      ) : null}
      {face.map((tag) => {
        const pressed = selected.includes(tag);
        return (
          <FilterChip key={tag} pressed={pressed} onClick={() => toggle(tag)}>
            {pressed ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
            {tag}
          </FilterChip>
        );
      })}
      {extraSelected.map((tag) => (
        <FilterChip key={tag} pressed onClick={() => toggle(tag)}>
          {tag}
          <X className="h-3.5 w-3.5" aria-hidden />
        </FilterChip>
      ))}
      {overflow.length > 0 ? (
        <MenuPopover
          align="start"
          title={t('forms.tags_label', 'Tags')}
          className="tag-pop-wrap"
          trigger={
            <FilterChip>
              {t('forms.tags_more', {
                count: overflow.length,
                defaultValue: `+${overflow.length} tags`,
              })}
            </FilterChip>
          }
        >
          <TagChecklist tags={available} selected={selected} onToggle={toggle} />
        </MenuPopover>
      ) : null}
    </div>
  );
}
